import {
  Button,
  Grid,
  Typography,
  Box,
  Paper,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useState } from 'react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useCloudSignInfo } from '@/hooks/useCloudSignInfo';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getPatient } from '@/graphql/queries';
import { useActiveStep } from '../hooks/useActiveStep';
import { updatePatient } from '@/graphql/mutations';
import { executeQuery } from '@/client/graphql';
import { GetPatientQuery, UpdatePatientMutation } from '@/API';
import { executePost } from '@/client/rest';

export function ComprehensiveConsent() {
  const [openDialog, setOpenDialog] = useState(false);

  const { setActiveStep } = useActiveStep();
  const { setCloudSignInfo } = useCloudSignInfo();

  async function agreeButtonOnClick() {
    setActiveStep('ConfirmationComprehensiveConsent');

    const postTokenResult = (await executePost({
      apiName: 'medirecoWebApi',
      path: '/cloud-sign/token'
    })) as any;

    const token = postTokenResult.access_token;

    const postDocumentsResult = executePost({
      apiName: 'medirecoWebApi',
      path: '/cloud-sign/documents',
      options: {
        body: {
          token
        }
      }
    }) as any;

    const documentId = postDocumentsResult.id;
    const participantId = postDocumentsResult.participants[1].id;

    setCloudSignInfo({ token, documentId, participantId });

    const session = await fetchAuthSession();

    const result = await executeQuery<GetPatientQuery>(
      getPatient,
      {
        id: session.userSub as string
      },
      'userPool'
    );

    const patientData = result?.getPatient;

    if (patientData) {
      const name = patientData.familyName + patientData.firstName;
      const email = patientData.email;

      const participantsRes = executePost({
        apiName: 'medirecoWebApi',
        path: '/cloud-sign/participants',
        options: {
          body: {
            token,
            documentId,
            participantId,
            name,
            email
          }
        }
      }) as any;

      await executePost({
        apiName: 'medirecoWebApi',
        path: '/cloud-sign/documents/id',
        options: {
          body: {
            token,
            documentId
          }
        }
      });
    }
  }

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDisagree = async () => {
    try {
      const session = await fetchAuthSession();
      if (session.userSub) {
        await executeQuery<UpdatePatientMutation>(updatePatient, {
          input: {
            id: session.userSub,
            houkatuDoui: false
          }
        });
      }
    } catch (e) {
      // TODO: fetchAuthSessionの例外処理を考える
      console.error(e);
    }
    setOpenDialog(false);
    setActiveStep('IntroductionOtherHospital');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          診療申込みと重要事項
        </Typography>
        <Typography style={{ marginBottom: '8px' }}>
          当院の治療方針について同意書(お客様ー病院間)で次の内容を説明しています。
          <br />
          同意していただきますようお願いいたします
        </Typography>

        <Paper style={{ padding: '10px', marginBottom: '20px' }}>
          <Typography>
            (予防の推進) <br />
            ・足病の再発または発症予防の指導、医療提案を行います
            <br />
            <br />
            (セルフケアのお願い)
            <br />
            ・自宅でのセルフケア
            <br />
            <br />
            (無料)
            <br />
            または自費診療でのケア行わない場合は再発の可能性があります <br />
            <br />
            (症状部位以外の検査)
            <br />
            ・比較のため症状のない側の足のレントゲも撮影します。
            <br />
            ・症状によっては胸椎や腰、股関節等のレントゲン検査を行う場合があります <br />
            <br /> (自費診察は希望者のみ)
            <br />
            ・自費検査やリハビリは保険診療実施の後検討が可能です <br />
            <br /> (院内の撮影)
            <br />
            ・安全確保と患者誘導の効率化から院内カメラが設置されています
          </Typography>
        </Paper>
        <Paper style={{ padding: '10px', marginBottom: '20px' }}>
          <Typography color="error">
            ※※診療情報の管理と利用※※
            <br />
            あなたの診療情報の利用について、個人が分からないように加工して当院との契約関係のある
            <br />
            第三者との研究、診療品質の向上のために利用します。
            <br />
            また、診療の目的の範囲で他の医療施設等に提供します。(他院での許諾を不要とする包括同意といいます)
            <br />
            また、院内カメラで撮影された情報は当院内の診療目的以外では利用いたしません。
          </Typography>
        </Paper>
        <Grid container spacing={2} display="flex" justifyContent="space-between">
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={() => {
                console.log('戻るの処理は未定');
              }}
              sx={{ margin: 2, width: '95%' }}
            >
              戻る
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" textAlign="center">
              契約内容に
            </Typography>
            <Box display="flex" justifyContent="space-between">
              <Button variant="outlined" fullWidth onClick={handleOpenDialog} sx={{ margin: 2 }}>
                同意しない
              </Button>
              <Button variant="contained" fullWidth onClick={() => agreeButtonOnClick()} sx={{ margin: 2 }}>
                同意する
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>確認</DialogTitle>
          <DialogContent>
            <DialogContentText>
              当院での治療方針に同意いただけない場合は、当院での治療ではお引き受けできない可能性がございます。
              よろしいでしょうか？
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} variant="contained">
              キャンセル
            </Button>
            <Button onClick={handleDisagree}>同意しない</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
